import React, { useMemo, useState, useEffect } from 'react';
import { ExternalLink, BadgeCheck, Search, TrendingUp, Users, RefreshCw, Loader2, Filter, ChevronDown, Quote, Layers, MessageSquareQuote, Pin, PinOff, Sparkles, Plus, Trash2, Download } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { inspirations as inspApi } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';
import { useAuth } from '../../lib/auth-context';
import PaywallContentGate from './PaywallContentGate';

const COUNTRIES = [
  // Americas
  'United States',
  'Canada',
  'Brazil',
  // Europe
  'United Kingdom',
  'Germany',
  'France',
  'Netherlands',
  // Middle East
  'United Arab Emirates',
  'Dubai',
  'Saudi Arabia',
  // South Asia
  'India',
  'Bangladesh',
  'Sri Lanka',
  'Maldives',
  // East Asia
  'China',
  'Hong Kong',
  'Macau',
  'Taiwan',
  'Japan',
  'South Korea',
  // South-East Asia
  'Singapore',
  'Malaysia',
  'Indonesia',
  'Thailand',
  'Vietnam',
  'Philippines',
  'Cambodia',
  'Myanmar',
  // Oceania
  'Australia',
  'VIC - Victoria',
  'QLD - Queensland',
];

const ENGAGEMENTS = ['High', 'Medium', 'Low'];

function LeaderCard({ leader, pin, onPin, onUnpin, selectable, selected, onToggleSelect }) {
  const linkedInUrl = leader.linkedin_url || leader.search_url || '';
  const isVerified = leader.confidence === 'verified';
  const hasPostingDNA = !!(leader.hook_style || leader.structure_style || leader.closing_style);
  const [openDna, setOpenDna] = useState(false);
  const isPinned = !!pin;
  // The leader id is whatever the leader object surfaces; we fall back to a
  // slug-from-url so pinning works even on the curated-without-id case.
  const leaderId = leader.id || leader.leader_id || (linkedInUrl.match(/\/in\/([^/?#]+)/) || [])[1] || leader.name;
  return (
    <Card className={`border-border h-full flex flex-col transition-colors ${selected ? 'ring-2 ring-primary border-primary' : ''}`} data-testid="thought-leader-card">
      <CardContent className="p-5 flex flex-col h-full">
        <div className="flex items-start gap-3">
          {selectable && (
            <label
              className="shrink-0 inline-flex items-center pt-1 cursor-pointer"
              data-testid="leader-select-label"
              title="Include in positioning analysis"
            >
              <input
                type="checkbox"
                checked={!!selected}
                onChange={() => onToggleSelect?.()}
                className="h-4 w-4 accent-primary cursor-pointer"
                data-testid="leader-select-checkbox"
              />
            </label>
          )}
          <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-secondary text-primary font-display font-bold">
            {(leader.name || '?').slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <h3 className="font-semibold truncate" data-testid="leader-name">{leader.name}</h3>
              {isVerified && <BadgeCheck size={14} className="text-primary shrink-0" aria-label="Verified profile" />}
            </div>
            <p className="text-xs text-muted-foreground truncate">
              {leader.title}{leader.company ? ` · ${leader.company}` : ''}
            </p>
            <div className="flex items-center gap-1 mt-1 flex-wrap">
              {leader.follower_band && (
                <Badge variant="secondary" className="font-mono-edge text-[10px]">{leader.follower_band} followers</Badge>
              )}
              {leader.country && (
                <Badge variant="secondary" className="font-mono-edge text-[10px]">{leader.country}</Badge>
              )}
              {linkedInUrl && (linkedInUrl.includes('/in/') || linkedInUrl.includes('/in?')) && onPin && (
                <Button
                  variant={isPinned ? 'default' : 'outline'}
                  size="sm"
                  className="h-6 px-2 text-[10px] font-medium"
                  onClick={() => (isPinned ? onUnpin(leaderId) : onPin({ leaderId, leaderName: leader.name, linkedinUrl: linkedInUrl }))}
                  data-testid={isPinned ? 'leader-unpin-btn' : 'leader-pin-btn'}
                >
                  {isPinned ? <PinOff size={11} className="mr-1" /> : <Pin size={11} className="mr-1" />}
                  {isPinned ? 'Pinned' : 'Pin to mine posts'}
                </Button>
              )}
            </div>
            {pin?.scrape_status === 'pending' && (
              <p className="text-[10px] text-muted-foreground mt-1 font-mono-edge" data-testid="leader-scrape-pending">
                Awaiting scrape — click &ldquo;Scrape pinned&rdquo; above.
              </p>
            )}
            {pin?.scrape_status === 'error' && (
              <p className="text-[10px] text-destructive mt-1" data-testid="leader-scrape-error">
                Scrape failed: {(pin.scrape_error || '').slice(0, 80) || 'unknown error'}
              </p>
            )}
          </div>
        </div>
        <p className="text-sm mt-3 flex-1">{leader.why_follow}</p>

        {leader.themes && leader.themes.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {leader.themes.map((t, i) => (
              <Badge key={i} variant="secondary" className="font-mono-edge text-[10px]">{t}</Badge>
            ))}
          </div>
        )}

        {/* How they post — hook / structure / closing */}
        {hasPostingDNA && (
          <div className="mt-3 rounded-lg border border-border bg-secondary/30 overflow-hidden" data-testid="leader-posting-dna">
            <button
              type="button"
              onClick={() => setOpenDna((o) => !o)}
              className="w-full flex items-center justify-between px-3 py-2 text-xs font-mono-edge tracking-[0.14em] text-primary hover:bg-secondary/50 transition-colors"
              data-testid="leader-posting-dna-toggle"
            >
              <span className="inline-flex items-center gap-1.5">
                <Layers size={12} />
                HOW THEY POST
              </span>
              <ChevronDown
                size={13}
                className="transition-transform"
                style={{ transform: openDna ? 'rotate(180deg)' : 'rotate(0deg)' }}
              />
            </button>
            {openDna && (
              <div className="px-3 pb-3 pt-1 space-y-2.5 text-xs" data-testid="leader-posting-dna-content">
                {leader.hook_style && (
                  <div className="flex items-start gap-2">
                    <Quote size={11} className="text-primary mt-0.5 shrink-0" />
                    <div>
                      <p className="font-mono-edge text-[10px] tracking-[0.14em] text-muted-foreground mb-0.5">
                        HOOK
                      </p>
                      <p className="text-foreground/85 leading-snug">{leader.hook_style}</p>
                    </div>
                  </div>
                )}
                {leader.structure_style && (
                  <div className="flex items-start gap-2">
                    <Layers size={11} className="text-primary mt-0.5 shrink-0" />
                    <div>
                      <p className="font-mono-edge text-[10px] tracking-[0.14em] text-muted-foreground mb-0.5">
                        STRUCTURE
                      </p>
                      <p className="text-foreground/85 leading-snug">{leader.structure_style}</p>
                    </div>
                  </div>
                )}
                {leader.closing_style && (
                  <div className="flex items-start gap-2">
                    <MessageSquareQuote size={11} className="text-primary mt-0.5 shrink-0" />
                    <div>
                      <p className="font-mono-edge text-[10px] tracking-[0.14em] text-muted-foreground mb-0.5">
                        CLOSE
                      </p>
                      <p className="text-foreground/85 leading-snug">{leader.closing_style}</p>
                    </div>
                  </div>
                )}
                {leader.signature_traits && leader.signature_traits.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {leader.signature_traits.map((t, i) => (
                      <Badge
                        key={i}
                        className="font-mono-edge text-[10px] bg-primary/10 text-primary border border-primary/20"
                      >
                        {t}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {linkedInUrl && (
          <a href={linkedInUrl} target="_blank" rel="noopener noreferrer" className="mt-4" data-testid="leader-link">
            <Button variant="secondary" size="sm" className="w-full">
              {leader.linkedin_url ? (
                <>Open profile <ExternalLink size={12} className="ml-1.5" /></>
              ) : (
                <>Find on LinkedIn <Search size={12} className="ml-1.5" /></>
              )}
            </Button>
          </a>
        )}

        {/* Scraped posts + clustered topics + avg engagement (only when leader was mined) */}
        {pin?.scrape_status === 'ready' && Array.isArray(pin?.posts) && pin.posts.length > 0 && (() => {
          const posts = pin.posts;
          const totalLikes = posts.reduce((s, p) => s + (Number(p.likes) || 0), 0);
          const totalComments = posts.reduce((s, p) => s + (Number(p.comments) || 0), 0);
          const avgEngagement = posts.length ? Math.round((totalLikes + totalComments) / posts.length) : 0;
          return (
            <div className="mt-4 pt-3 border-t border-border" data-testid="leader-scraped-posts">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-mono-edge tracking-[0.16em] uppercase text-muted-foreground">
                  What they&rsquo;re posting ({posts.length})
                </p>
                <Badge variant="secondary" className="font-mono-edge text-[10px]" data-testid="leader-avg-engagement">
                  Avg {avgEngagement.toLocaleString()} / post
                </Badge>
              </div>
              <ul className="space-y-2">
                {posts.slice(0, 3).map((p, idx) => (
                  <li key={idx} className="text-[11px] text-muted-foreground leading-snug">
                    <p className="line-clamp-3">
                      {(p.text || p.content || '').slice(0, 220)}{(p.text || p.content || '').length > 220 ? '…' : ''}
                    </p>
                    {((Number(p.likes) || 0) + (Number(p.comments) || 0) > 0) && (
                      <p className="text-[10px] font-mono-edge text-foreground/50 mt-0.5">
                        {(Number(p.likes) || 0).toLocaleString()} reactions · {(Number(p.comments) || 0).toLocaleString()} comments
                      </p>
                    )}
                  </li>
                ))}
              </ul>
              {Array.isArray(pin.topic_clusters) && pin.topic_clusters.length > 0 && (
                <div className="mt-3">
                  <p className="text-[10px] font-mono-edge tracking-[0.16em] uppercase text-muted-foreground mb-1.5">
                    Topic clusters
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {pin.topic_clusters.slice(0, 5).map((t, i) => (
                      <Badge key={i} variant="outline" className="text-[10px]">
                        <Sparkles size={9} className="mr-1" />
                        {t.topic || t.title || ''}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </CardContent>
    </Card>
  );
}

function engagementColor(level) {
  if (level === 'High') return 'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30';
  if (level === 'Medium') return 'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30';
  if (level === 'Low') return 'bg-muted text-muted-foreground border-border';
  return 'bg-secondary text-foreground border-border';
}

function TopicCard({ topic }) {
  const fromLeader = topic.from_leader || topic.source_leader || '';
  return (
    <Card className="border-border h-full flex flex-col" data-testid="trending-topic-card">
      <CardContent className="p-5 flex flex-col h-full">
        <div className="flex items-start justify-between gap-2 mb-2">
          <Badge variant="secondary" className="font-mono-edge text-[10px]">#{topic.id}</Badge>
          <div className="flex gap-1 flex-wrap justify-end">
            {fromLeader && (
              <Badge
                className="font-mono-edge text-[10px] bg-primary/10 text-primary border border-primary/30"
                data-testid="topic-from-inspiration"
              >
                <Sparkles size={9} className="mr-1" />
                From {fromLeader.length > 18 ? fromLeader.slice(0, 16) + '…' : fromLeader}
              </Badge>
            )}
            {topic.format_suggestion && (
              <Badge className="bg-[hsl(var(--mint))]/40 border border-border text-foreground font-mono-edge text-[10px]">
                {topic.format_suggestion}
              </Badge>
            )}
            {topic.engagement_level && (
              <Badge className={`font-mono-edge text-[10px] border ${engagementColor(topic.engagement_level)}`}>
                {topic.engagement_level}
              </Badge>
            )}
          </div>
        </div>
        <h3 className="font-semibold leading-tight" data-testid="topic-title">{topic.title || topic.topic}</h3>
        {topic.angle && <p className="text-sm text-muted-foreground mt-1">{topic.angle}</p>}
        {topic.hook_example && (
          <p className="text-sm italic mt-2 border-l-2 border-primary/40 pl-2.5">&ldquo;{topic.hook_example}&rdquo;</p>
        )}
        {topic.why_trending && (
          <p className="text-xs text-muted-foreground mt-3">
            <span className="font-mono-edge font-semibold">Why now:</span> {topic.why_trending}
          </p>
        )}
        {topic.search_urls && topic.search_urls.length > 0 && (
          <div className="mt-4 pt-3 border-t border-border space-y-1.5">
            <p className="text-[10px] font-mono-edge text-muted-foreground">SEE LIVE TRENDING POSTS ON LINKEDIN</p>
            {topic.search_urls.slice(0, 3).map((s, i) => (
              <a
                key={i}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between text-xs hover:underline text-primary group"
                data-testid="topic-search-link"
              >
                <span className="truncate">{s.query}</span>
                <ExternalLink size={11} className="ml-1 opacity-70 group-hover:opacity-100 shrink-0" />
              </a>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function InspirationTab({
  leaders = [],
  topics = [],
  defaultIndustry = '',
  activeFilters = {},
  refreshing = false,
  onRefresh,
  analysisId = '',
  inspirationPins = [],
  positioningReport: positioningReportProp = null,
  onInspirationDataChange,
  contentGated = false,
  onUpgrade,
}) {
  // Pin/unpin/scrape state ----------------------------------------------------
  const [pinBusy, setPinBusy] = useState(false);
  const [scrapeBusy, setScrapeBusy] = useState(false);
  const [quota, setQuota] = useState(null); // { limit, used_this_month, remaining }
  // Admin-only "add custom leader" state ---------------------------------------
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const [adminPinOpen, setAdminPinOpen] = useState(false);
  const [adminPinBusy, setAdminPinBusy] = useState(false);
  const ADMIN_POOL_CAP = 10;
  const adminPoolUsed = useMemo(
    () => (inspirationPins || []).filter((p) => (p?.source || '') === 'admin').length,
    [inspirationPins],
  );
  // Positioning analysis state (admin-only) -----------------------------------
  const [selectedSlugs, setSelectedSlugs] = useState(() => new Set());
  const [positioningBusy, setPositioningBusy] = useState(false);
  // The persisted report (if any) lives on the analysis doc; we mirror it
  // locally so a fresh run replaces the UI immediately.
  const [positioningReport, setPositioningReport] = useState(positioningReportProp || null);
  useEffect(() => {
    if (positioningReportProp) setPositioningReport(positioningReportProp);
  }, [positioningReportProp]);

  const readyPins = useMemo(
    () => (inspirationPins || []).filter((p) => (p?.scrape_status || '') === 'ready' && (p?.posts || []).length > 0),
    [inspirationPins],
  );
  const toggleSelectSlug = (slug) => {
    if (!slug) return;
    setSelectedSlugs((cur) => {
      const next = new Set(cur);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };
  const handlePositioning = async () => {
    if (!analysisId) return;
    const slugs = Array.from(selectedSlugs);
    if (slugs.length === 0) {
      toast.error('Select at least one scraped leader first.');
      return;
    }
    setPositioningBusy(true);
    const t = toast.loading(`Analyzing positioning for ${slugs.length} leader${slugs.length > 1 ? 's' : ''}…`);
    try {
      const r = await inspApi.generatePositioning(analysisId, slugs);
      setPositioningReport(r.positioning_report);
      onInspirationDataChange?.({ inspiration_positioning_report: r.positioning_report });
      toast.success('Positioning report ready', { id: t });
    } catch (e) {
      toast.error(getErrorMessage(e, 'Positioning analysis failed'), { id: t });
    } finally {
      setPositioningBusy(false);
    }
  };

  // Load quota once on mount (and after a successful scrape).
  const refreshQuota = async () => {
    try {
      const q = await inspApi.quota();
      setQuota(q);
    } catch {
      /* non-fatal */
    }
  };
  useEffect(() => {
    if (analysisId) refreshQuota();
  }, [analysisId]);
  const pinsByLeaderId = useMemo(() => {
    const m = {};
    (inspirationPins || []).forEach((p) => {
      if (p?.leader_id) m[p.leader_id] = p;
      if (p?.slug) m[p.slug] = p;
    });
    return m;
  }, [inspirationPins]);
  const pendingPinCount = useMemo(
    () => (inspirationPins || []).filter((p) => p?.scrape_status !== 'ready').length,
    [inspirationPins],
  );
  const handlePin = async ({ leaderId, leaderName, linkedinUrl }) => {
    if (!analysisId || pinBusy) return;
    setPinBusy(true);
    try {
      const r = await inspApi.pin(analysisId, { leaderId, leaderName, linkedinUrl });
      onInspirationDataChange?.({ inspiration_pins: r.pins });
      toast.success(r.already_pinned ? 'Already pinned.' : 'Pinned.');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not pin leader'));
    } finally {
      setPinBusy(false);
    }
  };
  const handleUnpin = async (leaderId) => {
    if (!analysisId || pinBusy) return;
    setPinBusy(true);
    try {
      const r = await inspApi.unpin(analysisId, leaderId);
      onInspirationDataChange?.({ inspiration_pins: r.pins });
      toast.success('Unpinned.');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not unpin leader'));
    } finally {
      setPinBusy(false);
    }
  };
  const handleScrape = async () => {
    if (!analysisId || scrapeBusy) return;
    setScrapeBusy(true);
    const t = toast.loading('Scraping pinned leaders… ~20–60s');
    try {
      const r = await inspApi.scrape(analysisId);
      onInspirationDataChange?.({
        inspiration_pins: r.pins,
        trending_topics: r.trending_topics,
      });
      if (r.scraped > 0) toast.success(`Scraped ${r.scraped} leader${r.scraped > 1 ? 's' : ''}. Topic suggestions updated.`, { id: t });
      else toast.info(r.message || 'Nothing to scrape.', { id: t });
      refreshQuota();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Scrape failed'), { id: t });
    } finally {
      setScrapeBusy(false);
    }
  };
  // Multi-select country state \u2014 stored as array; "[]" means Global.
  // Hydrate from activeFilters.country which may be a comma-joined string or array.
  const parseCountries = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.filter(Boolean);
    if (typeof raw === 'string') {
      if (raw === 'global') return [];
      return raw.split(',').map((s) => s.trim()).filter(Boolean);
    }
    return [];
  };
  const [selectedCountries, setSelectedCountries] = useState(
    parseCountries(activeFilters.country)
  );
  const [industry, setIndustry] = useState(activeFilters.industry || defaultIndustry || '');
  // Client-side engagement filter on trending topics
  const [enabledEngagements, setEnabledEngagements] = useState(new Set(ENGAGEMENTS));

  useEffect(() => {
    setSelectedCountries(parseCountries(activeFilters.country));
    if (activeFilters.industry) setIndustry(activeFilters.industry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilters.country, activeFilters.industry]);

  const toggleCountry = (c) => {
    setSelectedCountries((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]
    );
  };

  const toggleEngagement = (lvl) => {
    setEnabledEngagements((prev) => {
      const next = new Set(prev);
      if (next.has(lvl)) next.delete(lvl); else next.add(lvl);
      if (next.size === 0) return new Set(ENGAGEMENTS); // never empty
      return next;
    });
  };

  const filteredTopics = useMemo(() => {
    return (topics || []).filter((t) => {
      if (!t.engagement_level) return true;
      return enabledEngagements.has(t.engagement_level);
    });
  }, [topics, enabledEngagements]);

  const submitRefresh = () => {
    if (!onRefresh) return;
    const countryStr = selectedCountries.length === 0 ? null : selectedCountries.join(', ');
    onRefresh({ country: countryStr, industry: industry || null });
  };

  const activeCountryStr = (() => {
    const arr = parseCountries(activeFilters.country);
    return arr.length === 0 ? '' : arr.join(', ');
  })();

  const filtersChanged =
    activeCountryStr !== selectedCountries.join(', ') ||
    (activeFilters.industry || defaultIndustry || '') !== industry;

  return (
    <div className="space-y-8">
      {/* Filters bar */}
      <Card className="border-border" data-testid="inspiration-filters">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row lg:items-end gap-3 lg:gap-5">
            <div className="flex items-center gap-2 lg:mr-2">
              <Filter size={16} className="text-primary" />
              <span className="font-semibold">Filters</span>
            </div>
            <div className="flex-1 grid gap-3">
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Countries / regions (multi-select)</Label>
                  <div className="flex items-center gap-3 text-[11px]">
                    {selectedCountries.length === 0 ? (
                      <span className="text-muted-foreground">Global (anywhere)</span>
                    ) : (
                      <span className="text-foreground/80 font-mono-edge">
                        {selectedCountries.length} selected
                      </span>
                    )}
                    {selectedCountries.length > 0 && (
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground"
                        onClick={() => setSelectedCountries([])}
                        data-testid="filter-country-clear"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
                <div
                  className="flex flex-wrap gap-1.5"
                  data-testid="filter-country-chips"
                >
                  {COUNTRIES.map((c) => {
                    const selected = selectedCountries.includes(c);
                    return (
                      <button
                        type="button"
                        key={c}
                        onClick={() => toggleCountry(c)}
                        className={`rounded-full px-3 py-1 text-xs border transition-colors ${
                          selected
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-card text-foreground/80 border-border hover:border-primary/50'
                        }`}
                        data-testid={`filter-country-chip-${c.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Industry override</Label>
                <Input
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  placeholder={defaultIndustry || 'e.g., FinTech'}
                  data-testid="filter-industry-input"
                  className="max-w-md"
                />
              </div>
            </div>
            <Button
              onClick={submitRefresh}
              disabled={refreshing || !onRefresh || !filtersChanged}
              className="rounded-xl lg:self-end"
              data-testid="filter-refresh-button"
            >
              {refreshing ? <Loader2 size={14} className="animate-spin mr-2" /> : <RefreshCw size={14} className="mr-2" />}
              Refresh inspiration
            </Button>
          </div>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground font-mono-edge">ENGAGEMENT</span>
            {ENGAGEMENTS.map((lvl) => {
              const active = enabledEngagements.has(lvl);
              return (
                <button
                  key={lvl}
                  onClick={() => toggleEngagement(lvl)}
                  data-testid={`engagement-chip-${lvl}`}
                  className={`px-2.5 py-1 rounded-full border text-xs font-mono-edge transition-colors ${
                    active ? engagementColor(lvl) : 'bg-card text-muted-foreground border-border opacity-60'
                  }`}
                >
                  {lvl}
                </button>
              );
            })}
            {activeCountryStr && (
              <Badge variant="secondary" className="ml-auto font-mono-edge text-[10px]">
                Active: {activeCountryStr}{activeFilters.industry ? ` · ${activeFilters.industry}` : ''}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {refreshing && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="p-4 flex items-center gap-3">
            <Loader2 size={18} className="animate-spin text-primary" />
            <p className="text-sm">Refreshing thought-leaders and trending topics with your filters...</p>
          </CardContent>
        </Card>
      )}

      <section data-testid="thought-leaders-section">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Users size={18} className="text-primary" />
            <h2 className="text-xl font-semibold">Top profiles similar to yours</h2>
          </div>
          {analysisId && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="inspiration-scrape-bar">
              {(inspirationPins?.length > 0 || quota) && (
                <Badge
                  variant="secondary"
                  className="font-mono-edge text-[10px]"
                  data-testid="inspiration-quota-badge"
                >
                  {quota
                    ? `${quota.used_this_month ?? 0} / ${quota.limit ?? 5} scrapes this month`
                    : `${inspirationPins?.length || 0} pinned`}
                </Badge>
              )}
              {isAdmin && (
                <Badge
                  variant="outline"
                  className="font-mono-edge text-[10px] border-primary/40 text-primary"
                  data-testid="inspiration-admin-pool-badge"
                >
                  Admin pool {adminPoolUsed} / {ADMIN_POOL_CAP}
                </Badge>
              )}
              {inspirationPins?.length > 0 && (
                <Badge
                  variant="outline"
                  className="font-mono-edge text-[10px]"
                  data-testid="inspiration-pinned-count"
                >
                  {pendingPinCount > 0
                    ? `${pendingPinCount} pending · ${inspirationPins.length} total`
                    : `${inspirationPins.length} pinned · all scraped`}
                </Badge>
              )}
              {isAdmin && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setAdminPinOpen(true)}
                  disabled={adminPoolUsed >= ADMIN_POOL_CAP}
                  data-testid="inspiration-admin-add-btn"
                  className="rounded-xl border-primary/40 text-primary hover:bg-primary/5"
                >
                  <Plus size={14} className="mr-2" />
                  Add inspiration leader
                </Button>
              )}
              {isAdmin && readyPins.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handlePositioning}
                  disabled={positioningBusy || selectedSlugs.size === 0}
                  data-testid="inspiration-positioning-btn"
                  className="rounded-xl border-primary/40 text-primary hover:bg-primary/5"
                  title={selectedSlugs.size === 0 ? 'Select at least one scraped leader card first' : ''}
                >
                  {positioningBusy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Layers size={14} className="mr-2" />}
                  Positioning report ({selectedSlugs.size})
                </Button>
              )}
              {isAdmin && readyPins.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    const t = toast.loading('Building strategic positioning deck… (30-90s if first run)');
                    try {
                      const token = localStorage.getItem('le_token');
                      const res = await fetch(
                        `${(process.env.REACT_APP_BACKEND_URL || '') + '/api'}/analyze/${analysisId}/inspirations/positioning-deck.pptx`,
                        { headers: { Authorization: `Bearer ${token}` } },
                      );
                      if (!res.ok) {
                        let msg = `Failed (${res.status})`;
                        try { const j = await res.json(); msg = j?.detail?.message || j?.detail || msg; } catch (_) { /* ignore */ }
                        throw new Error(msg);
                      }
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `blayzrr-positioning-deck.pptx`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      URL.revokeObjectURL(url);
                      toast.success('Deck downloaded.', { id: t });
                    } catch (e) {
                      toast.error(e?.message || 'Deck failed', { id: t });
                    }
                  }}
                  className="rounded-xl border-primary/40 text-primary hover:bg-primary/5"
                  data-testid="inspiration-scrape-deck-btn"
                >
                  <Download size={14} className="mr-2" />
                  Download positioning deck
                </Button>
              )}
              <Button
                size="sm"
                onClick={handleScrape}
                disabled={
                  scrapeBusy ||
                  !inspirationPins?.length ||
                  pendingPinCount === 0 ||
                  (quota && quota.remaining <= 0)
                }
                data-testid="inspiration-scrape-btn"
                className="rounded-xl"
              >
                {scrapeBusy ? (
                  <Loader2 size={14} className="animate-spin mr-2" />
                ) : (
                  <Sparkles size={14} className="mr-2" />
                )}
                Scrape pinned leaders
              </Button>
            </div>
          )}
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          The 10 highest-visibility LinkedIn voices in your role and industry. Pin up to 10 leaders, then click <span className="font-mono-edge text-foreground/80">Scrape pinned leaders</span> to mine their last 6 posts (5 scrapes / month) and feed real topics into your suggestions.{isAdmin && <span className="block mt-1 text-primary/80"><span className="font-mono-edge font-semibold">Admin:</span> use <span className="font-mono-edge">Add inspiration leader</span> to paste any LinkedIn URL (separate 10-leader pool, scrapes immediately, bypasses the monthly quota).</span>}
        </p>
        {leaders.length === 0 && !(inspirationPins || []).some((p) => (p?.source || '') === 'admin') ? (
          <Card className="border-dashed border-border">
            <CardContent className="p-6 text-center text-muted-foreground">
              No thought-leaders generated yet.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-5">
            {/* Admin custom-added leaders surface ABOVE the LLM-generated list
                so they're easy to spot. Built from inspiration_pins where
                source='admin' since they aren't in the `leaders` array. */}
            {(inspirationPins || []).some((p) => (p?.source || '') === 'admin') && (
              <div data-testid="admin-pinned-leaders-grid">
                <p className="text-[10px] font-mono-edge tracking-[0.16em] uppercase text-primary mb-2">
                  Admin-added benchmarks
                </p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(inspirationPins || [])
                    .filter((p) => (p?.source || '') === 'admin')
                    .map((p, i) => {
                      const synthetic = {
                        name: p.leader_name || p.slug || 'Leader',
                        title: 'Custom benchmark',
                        linkedin_url: p.linkedin_url || '',
                        why_follow: 'Added by admin for direct benchmarking. Posts and topic clusters below come from this leader.',
                      };
                      const ready = (p?.scrape_status || '') === 'ready' && (p?.posts || []).length > 0;
                      return (
                        <LeaderCard
                          key={`admin-${p.slug || i}`}
                          leader={synthetic}
                          pin={p}
                          onPin={undefined}
                          onUnpin={undefined}
                          selectable={isAdmin && ready}
                          selected={isAdmin && selectedSlugs.has(p.slug)}
                          onToggleSelect={() => toggleSelectSlug(p.slug)}
                        />
                      );
                    })}
                </div>
              </div>
            )}
            <PaywallContentGate
              gated={contentGated}
              label="leader benchmarks"
              testId="inspiration-leaders-gate"
            >
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {leaders.map((l, i) => {
                  const slug = (l.linkedin_url || '').match(/\/in\/([^/?#]+)/);
                  const lid = l.id || l.leader_id || (slug && slug[1]) || l.name;
                  const pinForLeader = pinsByLeaderId[lid] || pinsByLeaderId[slug && slug[1]] || null;
                  const slugForCheckbox = pinForLeader?.slug || (slug && slug[1]) || '';
                  const ready = pinForLeader && (pinForLeader.scrape_status || '') === 'ready' && (pinForLeader.posts || []).length > 0;
                  return (
                    <LeaderCard
                      key={i}
                      leader={l}
                      pin={pinForLeader}
                      onPin={analysisId && !contentGated ? handlePin : undefined}
                      onUnpin={analysisId && !contentGated ? handleUnpin : undefined}
                      selectable={isAdmin && ready}
                      selected={isAdmin && slugForCheckbox && selectedSlugs.has(slugForCheckbox)}
                      onToggleSelect={() => toggleSelectSlug(slugForCheckbox)}
                    />
                  );
                })}
              </div>
            </PaywallContentGate>
          </div>
        )}
      </section>

      <section data-testid="trending-topics-section">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp size={18} className="text-primary" />
          <h2 className="text-xl font-semibold">Trending topics in your niche</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          {filteredTopics.length} of {topics.length} ideas matching your filters. Each links to LinkedIn search so you can see the actual posts driving the conversation.
        </p>
        {filteredTopics.length === 0 ? (
          <Card className="border-dashed border-border">
            <CardContent className="p-6 text-center text-muted-foreground">
              No trending topics match the active engagement filters. Try enabling more levels.
            </CardContent>
          </Card>
        ) : (
          <PaywallContentGate
            gated={contentGated}
            label="trending topics"
            testId="inspiration-topics-gate"
          >
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTopics.map((t, i) => <TopicCard key={i} topic={t} />)}
            </div>
          </PaywallContentGate>
        )}
      </section>
      {isAdmin && positioningReport && (
        <PositioningReportSection
          report={positioningReport}
          analysisId={analysisId}
          isAdmin={isAdmin}
        />
      )}
      {/* Admin-only: add custom LinkedIn URL dialog */}
      {adminPinOpen && (
        <AdminAddLeaderDialog
          analysisId={analysisId}
          busy={adminPinBusy}
          onBusyChange={setAdminPinBusy}
          adminPoolUsed={adminPoolUsed}
          adminPoolCap={ADMIN_POOL_CAP}
          onClose={() => setAdminPinOpen(false)}
          onPinned={(payload) => {
            // Backend returns the full updated pins via subsequent reload;
            // but we get the freshly-scraped pin in payload.pin. Merge into
            // local state for instant feedback.
            const next = (inspirationPins || []).filter(
              (p) => (p?.slug || '') !== payload.pin?.slug,
            );
            next.push(payload.pin);
            onInspirationDataChange?.({ inspiration_pins: next });
            setAdminPinOpen(false);
          }}
        />
      )}
    </div>
  );
}

function AdminAddLeaderDialog({
  analysisId,
  busy,
  onBusyChange,
  adminPoolUsed,
  adminPoolCap,
  onClose,
  onPinned,
}) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [scrapeNow, setScrapeNow] = useState(true);

  const onSubmit = async () => {
    if (!name.trim()) {
      toast.error('Leader name is required');
      return;
    }
    if (!url.trim()) {
      toast.error('LinkedIn URL is required');
      return;
    }
    onBusyChange(true);
    const t = toast.loading(scrapeNow ? 'Adding leader & scraping…' : 'Adding leader…');
    try {
      const r = await inspApi.adminPin(analysisId, {
        leaderName: name.trim(),
        linkedinUrl: url.trim(),
        scrapeNow,
      });
      toast.success(
        `Added ${name.trim()}.${scrapeNow ? ' Topics merged into trending.' : ''}`,
        { id: t },
      );
      onPinned(r);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not add leader'), { id: t });
    } finally {
      onBusyChange(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg" data-testid="inspiration-admin-add-dialog">
        <DialogHeader>
          <DialogTitle>Add inspiration leader (admin)</DialogTitle>
          <DialogDescription>
            Paste any public LinkedIn profile to benchmark against. Admin-pool slots:{' '}
            <span className="font-mono-edge">{adminPoolUsed} / {adminPoolCap}</span>. This pool is
            separate from the user&apos;s 10-pin cap and bypasses the 5 / month scrape quota.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="ai-name" className="text-xs">Leader name</Label>
            <Input
              id="ai-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Satya Nadella"
              data-testid="inspiration-admin-name-input"
              maxLength={120}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ai-url" className="text-xs">LinkedIn URL</Label>
            <Input
              id="ai-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.linkedin.com/in/satya-nadella/"
              data-testid="inspiration-admin-url-input"
            />
          </div>
          <div className="flex items-center gap-3 pt-1">
            <Switch
              checked={scrapeNow}
              onCheckedChange={setScrapeNow}
              id="ai-scrape"
              data-testid="inspiration-admin-scrape-switch"
            />
            <Label htmlFor="ai-scrape" className="text-sm cursor-pointer">
              Scrape last 6 posts immediately
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={onSubmit}
            disabled={busy || adminPoolUsed >= adminPoolCap}
            className="rounded-xl"
            data-testid="inspiration-admin-submit-btn"
          >
            {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Plus size={14} className="mr-2" />}
            Add leader
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PositioningReportSection({ report, analysisId, isAdmin }) {
  const perLeader = Array.isArray(report?.per_leader) ? report.per_leader : [];
  const cohort = report?.cohort_overview || {};
  const distinctive = report?.your_distinctive_position || {};
  const recommendedTopics = Array.isArray(report?.recommended_post_topics)
    ? report.recommended_post_topics.filter((t) => t && (t.topic || '').trim())
    : [];
  const [downloading, setDownloading] = useState(null); // 'pdf' | 'pptx' | null

  const downloadBlob = (blob, ext) => {
    const href = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = `positioning-report-${new Date().toISOString().slice(0, 10)}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(href);
  };

  const onDownload = async (kind) => {
    if (!analysisId) return;
    setDownloading(kind);
    const t = toast.loading(`Preparing ${kind.toUpperCase()}…`);
    try {
      const blob = kind === 'pdf'
        ? await inspApi.downloadPositioningPdf(analysisId)
        : await inspApi.downloadPositioningPptx(analysisId);
      downloadBlob(blob, kind);
      toast.success(`${kind.toUpperCase()} downloaded`, { id: t });
    } catch (e) {
      toast.error(getErrorMessage(e, `${kind.toUpperCase()} download failed`), { id: t });
    } finally {
      setDownloading(null);
    }
  };

  return (
    <section className="mt-12" data-testid="positioning-report-section" style={{ fontFamily: 'Figtree, system-ui, sans-serif' }}>
      {/* Editorial header — eyebrow + serif headline + accent bar */}
      <div className="mb-6 pb-5 border-b-2 border-foreground/80">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[10px] font-semibold tracking-[0.22em] uppercase" style={{ color: '#b75a2e' }}>
              Strategic Positioning Report
            </p>
            <div className="w-12 h-[2px] mt-2 mb-3" style={{ background: '#b75a2e' }} />
            <h2 className="text-2xl md:text-3xl leading-tight" style={{ fontFamily: 'Playfair Display, Georgia, serif', fontWeight: 500 }}>
              A point of view on where you can lead the conversation.
            </h2>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline"
                onClick={() => onDownload('pdf')}
                disabled={!!downloading}
                data-testid="positioning-download-pdf-btn"
                className="rounded-xl"
              >
                {downloading === 'pdf' ? <Loader2 size={14} className="animate-spin mr-2" /> : <Quote size={14} className="mr-2" />}
                Download PDF
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onDownload('pptx')}
                disabled={!!downloading}
                data-testid="positioning-download-pptx-btn"
                className="rounded-xl"
              >
                {downloading === 'pptx' ? <Loader2 size={14} className="animate-spin mr-2" /> : <Layers size={14} className="mr-2" />}
                Download PPT
              </Button>
            </div>
          )}
        </div>
        <div className="mt-4 flex items-baseline justify-between gap-3 flex-wrap">
          <p className="text-xs tracking-[0.16em] uppercase text-muted-foreground font-medium">
            {report?.selected_count || perLeader.length} leaders analysed
          </p>
          {report?.generated_at && (
            <p className="text-[11px] font-mono-edge text-muted-foreground">
              Generated {new Date(report.generated_at).toLocaleString()}
            </p>
          )}
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-8 max-w-3xl leading-relaxed">
        For each selected leader we surface the position they own, the audience they resonate with, the authority signals they lean on, and the white space they leave open. The final card proposes a distinctive position you can credibly claim and three starter post themes to occupy it.
      </p>

      {perLeader.length > 0 && (
        <div className="grid lg:grid-cols-2 gap-6 mb-8" data-testid="positioning-per-leader-grid">
          {perLeader.map((row, i) => (
            <Card key={i} className="border-border bg-card h-full">
              <CardContent className="p-6 flex flex-col h-full">
                <div className="flex items-baseline gap-3 mb-4 pb-3 border-b border-border">
                  <span className="text-2xl" style={{ fontFamily: 'Playfair Display, serif', color: '#b75a2e' }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="text-xl leading-tight flex-1" style={{ fontFamily: 'Playfair Display, serif', fontWeight: 500 }}>
                    {row.leader_name || 'Leader'}
                  </h3>
                  {row.slug && (
                    <span className="text-[10px] font-mono-edge text-muted-foreground">{row.slug}</span>
                  )}
                </div>
                {row.positioning && (
                  <blockquote className="pl-4 border-l-[3px] mb-5 italic leading-relaxed" style={{ borderColor: '#b75a2e', fontFamily: 'Playfair Display, serif', fontSize: '1.05rem' }}>
                    {row.positioning}
                    <footer className="not-italic mt-2 text-[10px] tracking-[0.16em] uppercase text-muted-foreground font-medium" style={{ fontFamily: 'Figtree, sans-serif' }}>
                      The position {row.leader_name || 'they'} occupies
                    </footer>
                  </blockquote>
                )}
                {row.audience_resonance?.primary_audience && (
                  <div className="mb-5">
                    <p className="text-[10px] font-semibold tracking-[0.22em] uppercase mb-2" style={{ color: '#b75a2e' }}>
                      Audience &amp; subjects
                    </p>
                    <p className="text-sm" style={{ fontFamily: 'Playfair Display, serif', fontSize: '1rem' }}>
                      {row.audience_resonance.primary_audience}
                    </p>
                    {Array.isArray(row.audience_resonance.resonant_subjects) && row.audience_resonance.resonant_subjects.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {row.audience_resonance.resonant_subjects.map((s, j) => (
                          <Badge key={j} variant="outline" className="rounded-full font-normal text-[10px]">{s}</Badge>
                        ))}
                      </div>
                    )}
                    {row.audience_resonance.evidence && (
                      <p className="text-[11px] text-muted-foreground italic mt-2 leading-relaxed">
                        {row.audience_resonance.evidence}
                      </p>
                    )}
                  </div>
                )}
                {Array.isArray(row.authority_signals) && row.authority_signals.length > 0 && (
                  <div className="mb-5">
                    <p className="text-[10px] font-semibold tracking-[0.22em] uppercase mb-2" style={{ color: '#b75a2e' }}>
                      Authority signals
                    </p>
                    <ul className="space-y-1.5 text-sm">
                      {row.authority_signals.map((s, j) => (
                        <li key={j} className="flex gap-2 leading-relaxed">
                          <span style={{ color: '#b75a2e' }} className="font-semibold">—</span>
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {Array.isArray(row.gaps_and_whitespace) && row.gaps_and_whitespace.length > 0 && (
                  <div className="mt-auto pt-4 border-t-2" style={{ borderColor: '#b75a2e' }}>
                    <p className="text-[10px] font-semibold tracking-[0.22em] uppercase mb-2" style={{ color: '#b75a2e' }}>
                      Gaps &amp; whitespace
                    </p>
                    <p className="text-sm mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                      What {row.leader_name || 'they'} doesn&rsquo;t cover.
                    </p>
                    <ul className="space-y-1.5 text-sm">
                      {row.gaps_and_whitespace.map((g, j) => (
                        <li key={j} className="flex gap-2 leading-relaxed">
                          <span style={{ color: '#b75a2e' }} className="font-semibold">—</span>
                          <span>{g}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {(cohort.shared_themes || cohort.crowded_positions || cohort.collective_whitespace) && (
        <Card className="border-border bg-card mb-8">
          <CardContent className="p-6">
            <p className="text-[10px] font-semibold tracking-[0.22em] uppercase mb-2" style={{ color: '#b75a2e' }}>
              Cohort overview
            </p>
            <div className="w-12 h-[2px] mb-4" style={{ background: '#b75a2e' }} />
            <h3 className="text-xl mb-5" style={{ fontFamily: 'Playfair Display, serif', fontWeight: 500 }}>
              Where the cohort agrees, and where it doesn&rsquo;t.
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              {Array.isArray(cohort.shared_themes) && cohort.shared_themes.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold tracking-[0.22em] uppercase mb-2 text-muted-foreground">
                    Shared themes
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {cohort.shared_themes.map((t, i) => (
                      <Badge key={i} variant="outline" className="rounded-full font-normal text-[11px]">{t}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {cohort.crowded_positions && (
                <div>
                  <p className="text-[10px] font-semibold tracking-[0.22em] uppercase mb-2 text-muted-foreground">
                    Crowded positions
                  </p>
                  <p className="text-sm leading-relaxed">{cohort.crowded_positions}</p>
                </div>
              )}
              {Array.isArray(cohort.collective_whitespace) && cohort.collective_whitespace.length > 0 && (
                <div className="md:border-l-2 md:pl-5" style={{ borderColor: '#b75a2e' }}>
                  <p className="text-[10px] font-semibold tracking-[0.22em] uppercase mb-2" style={{ color: '#b75a2e' }}>
                    Collective whitespace
                  </p>
                  <p className="text-sm mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    What none of them own &mdash; yet.
                  </p>
                  <ul className="space-y-1.5 text-sm">
                    {cohort.collective_whitespace.map((g, j) => (
                      <li key={j} className="flex gap-2 leading-relaxed">
                        <span style={{ color: '#b75a2e' }} className="font-semibold">—</span>
                        <span>{g}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {(distinctive.headline || distinctive.why_credible) && (
        <Card className="border-0 text-white" data-testid="positioning-distinctive-card" style={{ background: '#1a1a1a' }}>
          <CardContent className="p-8 md:p-10">
            <p className="text-[10px] font-semibold tracking-[0.22em] uppercase mb-3" style={{ color: '#f0d9c9' }}>
              Your distinctive position
            </p>
            <div className="w-12 h-[2px] mb-5" style={{ background: '#f0d9c9' }} />
            {distinctive.headline && (
              <h3 className="text-2xl md:text-3xl leading-tight mb-5" style={{ fontFamily: 'Playfair Display, serif', fontWeight: 500, color: '#fbf9f5' }} data-testid="positioning-distinctive-headline">
                {distinctive.headline}
              </h3>
            )}
            {distinctive.why_credible && (
              <p className="italic leading-relaxed mb-7 max-w-3xl" style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.1rem', color: 'rgba(251,249,245,0.88)' }}>
                {distinctive.why_credible}
              </p>
            )}
            <div className="grid md:grid-cols-2 gap-6">
              {Array.isArray(distinctive.evidence_from_profile) && distinctive.evidence_from_profile.length > 0 && (
                <div className="border p-5" style={{ borderColor: 'rgba(240,217,201,0.25)' }}>
                  <p className="text-[10px] font-semibold tracking-[0.22em] uppercase mb-3" style={{ color: '#f0d9c9' }}>
                    Why you can credibly claim this
                  </p>
                  <ul className="space-y-2 text-sm" style={{ color: 'rgba(251,249,245,0.92)' }}>
                    {distinctive.evidence_from_profile.map((e, i) => (
                      <li key={i} className="flex gap-2 leading-relaxed">
                        <span style={{ color: '#f0d9c9' }} className="font-semibold">—</span>
                        <span>{e}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {Array.isArray(distinctive.first_three_post_themes) && distinctive.first_three_post_themes.length > 0 && (
                <div className="border p-5" style={{ borderColor: 'rgba(240,217,201,0.25)' }}>
                  <p className="text-[10px] font-semibold tracking-[0.22em] uppercase mb-3" style={{ color: '#f0d9c9' }}>
                    First three posts to occupy this position
                  </p>
                  <ol className="space-y-3">
                    {distinctive.first_three_post_themes.map((t, i) => (
                      <li key={i} className="flex items-baseline gap-3" style={{ color: '#fbf9f5' }}>
                        <span className="italic" style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.2rem', color: '#f0d9c9' }}>
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <div>
                          <p className="text-sm leading-snug" style={{ fontFamily: 'Playfair Display, serif', fontSize: '0.95rem' }}>
                            {t.theme}
                          </p>
                          {t.why && (
                            <p className="text-xs mt-1 leading-relaxed" style={{ color: 'rgba(251,249,245,0.65)' }}>
                              {t.why}
                            </p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {recommendedTopics.length > 0 && (
                <div
                  className="border p-5 mt-5"
                  style={{ borderColor: 'rgba(240,217,201,0.25)' }}
                  data-testid="positioning-recommended-topics"
                >
                  <div className="flex items-baseline justify-between gap-3 mb-3">
                    <p className="text-[10px] font-semibold tracking-[0.22em] uppercase" style={{ color: '#f0d9c9' }}>
                      Topics to occupy these gaps
                    </p>
                    <span className="text-[10px] font-mono tracking-[0.18em]" style={{ color: 'rgba(251,249,245,0.55)' }}>
                      {recommendedTopics.length} ideas
                    </span>
                  </div>
                  <p className="text-xs mb-4 leading-relaxed" style={{ color: 'rgba(251,249,245,0.65)' }}>
                    Each one sits in cohort whitespace AND is credibly ownable by you. Use the Regenerate Posts dialog on the audit page to publish them.
                  </p>
                  <ol className="grid md:grid-cols-2 gap-x-6 gap-y-3">
                    {recommendedTopics.map((t, i) => (
                      <li
                        key={i}
                        className="flex items-baseline gap-3"
                        style={{ color: '#fbf9f5' }}
                        data-testid={`positioning-recommended-topic-${i}`}
                      >
                        <span
                          className="italic shrink-0"
                          style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.05rem', color: '#f0d9c9', width: '1.5rem' }}
                        >
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <div className="min-w-0">
                          <p
                            className="text-sm leading-snug"
                            style={{ fontFamily: 'Playfair Display, serif', fontSize: '0.9rem' }}
                          >
                            {t.topic}
                          </p>
                          {t.why && (
                            <p
                              className="text-[11px] mt-1 leading-relaxed"
                              style={{ color: 'rgba(251,249,245,0.6)' }}
                            >
                              {t.why}
                            </p>
                          )}
                          {t.gap_source && (
                            <p
                              className="text-[9px] mt-1 tracking-[0.16em] uppercase"
                              style={{ color: 'rgba(240,217,201,0.75)' }}
                            >
                              {t.gap_source}
                            </p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </section>
  );
}

